import { isBlank } from '../common/util';
import type {
  BandKey,
  DistributionMode,
  OutletKey,
  RadioPackageDetail,
  RadioPackageDistribution,
  RadioPackageOption,
  RadioPackagePricing,
} from '../types';

export type PackagePricingKind = RadioPackagePricing['kind'];

/** Field path within one option: the option screens edit a bare option, while the
 * create screen nests the same fields under `options[0]`. */
export const optionField = (name: string, field: string): string => (name ? `${name}.${field}` : field);

/** Admin wording: what the option owns, rather than how a plan applies it. */
export const catalogPricingKindLabel = (kind: PackagePricingKind): string =>
  kind === 'RATE_CARD' ? 'Package ratecard' : 'Outlet discounts';

type RateCardPricing = Extract<RadioPackagePricing, { kind: 'RATE_CARD' }>;
type DiscountPricing = Extract<RadioPackagePricing, { kind: 'DISCOUNT' }>;

export type PricingDrafts = {
  RATE_CARD: RateCardPricing;
  DISCOUNT: DiscountPricing;
};

export type PackageOptionFormValues = {
  name: string;
  pricing: RadioPackagePricing;
  pricing_drafts: PricingDrafts;
  distribution: RadioPackageDistribution | null;
};

export type PackageMetadataFormValues = {
  name: string;
  terms: string;
};

export type PackageCreateFormValues = PackageMetadataFormValues & {
  options: [PackageOptionFormValues];
};

// Pricing and distribution are plain JSON subtrees of the form state, so the
// editor's drafts are structured clones rather than hand-written copies.
const cloneDistribution = (distribution: RadioPackageDistribution | null): RadioPackageDistribution | null =>
  distribution ? structuredClone(distribution) : null;

export const createPackagePricing = <K extends PackagePricingKind>(
  kind: K,
): Extract<RadioPackagePricing, { kind: K }> =>
  (kind === 'RATE_CARD'
    ? { kind: 'RATE_CARD', card: { rates: {} } }
    : { kind: 'DISCOUNT', percentages: {} }) as Extract<RadioPackagePricing, { kind: K }>;

export const createPackageOption = (): PackageOptionFormValues => {
  const pricing = createPackagePricing('RATE_CARD');
  return {
    name: '',
    pricing,
    pricing_drafts: {
      RATE_CARD: structuredClone(pricing),
      DISCOUNT: createPackagePricing('DISCOUNT'),
    },
    distribution: null,
  };
};

export const createRadioPackage = (): PackageCreateFormValues => ({
  name: '',
  terms: '',
  options: [createPackageOption()],
});

export function toPackageMetadataFormValues(radioPackage: RadioPackageDetail): PackageMetadataFormValues {
  return { name: radioPackage.name, terms: radioPackage.terms };
}

export function toPackageOptionFormValues(option: RadioPackageOption): PackageOptionFormValues {
  const pricing = structuredClone(option.pricing);
  return {
    name: option.name,
    pricing,
    pricing_drafts: {
      RATE_CARD: pricing.kind === 'RATE_CARD' ? structuredClone(pricing) : createPackagePricing('RATE_CARD'),
      DISCOUNT: pricing.kind === 'DISCOUNT' ? structuredClone(pricing) : createPackagePricing('DISCOUNT'),
    },
    distribution: cloneDistribution(option.distribution),
  };
}

export function switchPackagePricing(
  pricing: RadioPackagePricing,
  drafts: PricingDrafts,
  nextKind: PackagePricingKind,
): { pricing: RadioPackagePricing; pricing_drafts: PricingDrafts } {
  if (pricing.kind === nextKind) {
    return { pricing, pricing_drafts: drafts };
  }

  const nextDrafts: PricingDrafts = {
    ...drafts,
    [pricing.kind]: structuredClone(pricing),
  };
  return {
    pricing: structuredClone(nextDrafts[nextKind]),
    pricing_drafts: nextDrafts,
  };
}

export const createDistribution = (mode: DistributionMode): RadioPackageDistribution =>
  ({ mode, spots: {} }) as RadioPackageDistribution;

const compactCounts = <T extends object>(counts: T | undefined): T =>
  Object.fromEntries(Object.entries(counts ?? {}).filter(([, count]) => !isBlank(count))) as T;

/** Cells the planner cleared are absent, not null. A `WEEK_ALIGNED` band whose
 * every weekday was cleared drops out with them — an empty band would otherwise
 * read as "present, expecting zero everywhere". */
function compactSpots(distribution: RadioPackageDistribution): RadioPackageDistribution {
  if (distribution.mode === 'PERIOD_TOTAL') {
    return { mode: 'PERIOD_TOTAL', spots: compactCounts(distribution.spots) };
  }
  const spots = Object.entries(distribution.spots)
    .map(([bandKey, counts]) => [bandKey, compactCounts(counts)] as const)
    .filter(([, counts]) => Object.keys(counts).length > 0);
  return { mode: 'WEEK_ALIGNED', spots: Object.fromEntries(spots) };
}

export function normalizePackageOption(values: PackageOptionFormValues): object {
  return {
    name: values.name,
    pricing: structuredClone(values.pricing),
    distribution: values.distribution ? compactSpots(values.distribution) : null,
  };
}

export function normalizePackageCreate(values: PackageCreateFormValues): object {
  return {
    name: values.name,
    terms: values.terms,
    options: values.options.map(normalizePackageOption),
  };
}

export function normalizePackageMetadata(values: PackageMetadataFormValues): object {
  return { name: values.name, terms: values.terms };
}

export function getParticipatingOutletKeys(pricing: RadioPackagePricing): OutletKey[] {
  if (pricing.kind === 'DISCOUNT') {
    return (Object.entries(pricing.percentages) as [OutletKey, unknown][])
      .filter(([, percentage]) => !isBlank(percentage))
      .map(([outletKey]) => outletKey);
  }

  const outletKeys = new Set<OutletKey>();
  Object.values(pricing.card.rates).forEach((rates) => {
    (Object.entries(rates || {}) as [OutletKey, unknown][]).forEach(([outletKey, rate]) => {
      if (!isBlank(rate)) outletKeys.add(outletKey);
    });
  });
  return [...outletKeys];
}

export function getDistributionTotal(distribution: RadioPackageDistribution | null): number {
  if (!distribution) return 0;
  const counts =
    distribution.mode === 'PERIOD_TOTAL'
      ? Object.values(distribution.spots)
      : Object.values(distribution.spots).flatMap((spots) => Object.values(spots || {}));
  return counts.reduce<number>((total, count) => {
    const parsed = Number(count);
    return Number.isFinite(parsed) ? total + parsed : total;
  }, 0);
}

export type DistributionSpotValue = number | null | undefined;
export type DistributionBandSpots = Partial<Record<BandKey, DistributionSpotValue>>;
